import React from "react";
import { useTranslation } from "react-i18next";
import { WebhookStatsSummary } from "../../types/webhook";

interface WebhookStatsProps {
  stats: WebhookStatsSummary;
}

const WebhookStats: React.FC<WebhookStatsProps> = ({ stats }) => {
  const { t } = useTranslation("webhooks");

  const formatNumber = (num: number | undefined) => {
    if (num === undefined || num === null) return "0";
    return new Intl.NumberFormat().format(num);
  };

  const formatPercentage = (num: number | undefined) => {
    if (num === undefined || num === null) return "0.0%";
    return `${num.toFixed(1)}%`;
  };

  // Безопасные значения с проверкой на undefined
  const safeStats = {
    total_webhooks: stats?.webhooks?.total ?? 0,
    active_webhooks: stats?.webhooks?.active ?? 0,
    total_sent: stats?.logs?.total ?? 0,
    success_count: stats?.logs?.success ?? 0,
    failed_count: stats?.logs?.failed ?? 0,
    success_rate: stats?.logs?.success_rate ?? 0,
  };

  return (
    <div className="mb-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t("stats")}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Total Webhooks */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t("stats_summary.total_webhooks")}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(safeStats.total_webhooks)}
              </p>
            </div>
          </div>
        </div>

        {/* Active Webhooks */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t("stats_summary.active_webhooks")}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(safeStats.active_webhooks)}
              </p>
            </div>
          </div>
        </div>

        {/* Total Sent */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t("stats_summary.total_sent")}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(safeStats.total_sent)}
              </p>
            </div>
          </div>
        </div>

        {/* Success Count */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-emerald-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t("stats_summary.success_count")}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(safeStats.success_count)}
              </p>
            </div>
          </div>
        </div>

        {/* Failed Count */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t("stats_summary.failed_count")}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatNumber(safeStats.failed_count)}
              </p>
            </div>
          </div>
        </div>

        {/* Success Rate */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t("stats_summary.success_rate")}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatPercentage(safeStats.success_rate)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebhookStats;
